import { Trans } from '@lingui/react';
import { Card } from '@startuptools/ui/Card';
import { IconButton } from '@startuptools/ui/IconButton';
import { Stack } from '@startuptools/ui/Stack';
import { Table } from '@startuptools/ui/Table';
import { Typography } from '@startuptools/ui/Typography';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Suspense, useState } from 'react';
import {
  type GqlShareholdersQuery,
  ShareholdersDocument,
  useShareholdersSuspenseQuery,
} from '../../../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import { IdentityEditableEmail } from '../../../../components/editable-email/IdentityEditableEmail';
import { type ArrayElement } from '@startuptools/common/common';
import { IndividualReportShTable } from './IndividualReportInnerTable';

const IndividualReportRow = ({
  shareholder: { id, identity, identityId },
  showAvatar,
}: {
  shareholder: ArrayElement<GqlShareholdersQuery['shareholders']>;
  showAvatar: boolean;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <tr>
        <td>
          <IconButton variant="outlined" onClick={() => setOpen(!open)}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </td>
        <td>{identity && <Identity identity={identity} editable={false} showAvatar={showAvatar} />}</td>
        <td>
          <IdentityEditableEmail id={identityId} refetchQueries={[ShareholdersDocument]}>
            {identity?.email}
          </IdentityEditableEmail>
        </td>
        <td>{identity?.phone}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={4}>
            <Stack gap={1}>
              <IndividualReportShTable shareholderId={id} />
            </Stack>
          </td>
        </tr>
      )}
    </>
  );
};

const IndividualReportInner = () => {
  const companyId = useCompanyId();

  // fetchPolicy: no-cache until creation of shareholders trigger cache invalidation (when ShareRegister is fully in React)
  const {
    data: { shareholders },
  } = useShareholdersSuspenseQuery({ variables: { companyId }, fetchPolicy: 'no-cache' });

  return (
    <Stack gap={2}>
      <Typography level="h2">
        <Trans id="Individual Report" />
      </Typography>
      <Card>
        <Table sx={{ '& th:first-of-type': { width: '50px' } }}>
          <thead>
            <tr>
              <th />
              <th>
                <Trans id="Owner" />
              </th>
              <th>
                <Trans id="Email" />
              </th>
              <th>
                <Trans id="Phone" />
              </th>
            </tr>
          </thead>
          <tbody>
            {shareholders.map(sh => (
              <IndividualReportRow key={sh.id} shareholder={sh} showAvatar={shareholders.length <= 50} />
            ))}
          </tbody>
        </Table>
      </Card>
    </Stack>
  );
};

export const IndividualReport = () => {
  return (
    <Suspense>
      <IndividualReportInner />
    </Suspense>
  );
};
